import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Linking,
  Image,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const PublicBookingsAnalyticsDashboard = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none');
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  const fetchUserData = () => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => setUserData(data))
      .catch((error) => {
        console.error('Error fetching user data: ', error);
      });
  };

  const fetchDashboard = async () => {
    try {
      const response = await fetch(
        'https://collabbayadmin.com/APIControllers/FetchAllPublicBookingsAnalytics.php',
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const data = await response.json();

      if (data.success) {
        setDashboardData(data.data);
      } else {
        setDashboardData(null);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch(
      'https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php',
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';

        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      fetchUserData();
      await fetchDashboard();
    };
    fetchAll();
  }, []);

  const summary = dashboardData?.summary || {
    totalBookings: 0,
    bookingsLast30Days: 0,
    bookingsLast7Days: 0,
    upcomingBookings: 0,
  };

  const leaderboard = dashboardData?.leaderboard || [];
  const venueLeaderboard = dashboardData?.venueLeaderboard || [];
  const recentBookings = dashboardData?.recentBookings || [];
  const upcomingBookings = dashboardData?.upcomingBookings || [];
  const chartData = dashboardData?.chartData || [];
  const brandBreakdown = dashboardData?.brandBreakdown || [];

  const maxChartCount = useMemo(() => {
    if (!chartData.length) return 1;
    return Math.max(...chartData.map((item) => Number(item.count) || 0), 1);
  }, [chartData]);

  const filteredRecentBookings = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return recentBookings;

    return recentBookings.filter((item) => {
      return (
        (item.BrandName || '').toLowerCase().includes(q) ||
        (item.Promoter || '').toLowerCase().includes(q) ||
        (item.Name || '').toLowerCase().includes(q) ||
        (item.Email || '').toLowerCase().includes(q) ||
        (item.VenueOffer || '').toLowerCase().includes(q)
      );
    });
  }, [recentBookings, searchQuery]);

  const renderKpiCard = (label, value, icon) => (
    <View style={styles.kpiCard}>
      <View style={styles.kpiTopRow}>
        <View style={styles.kpiIconWrap}>
          <Ionicons name={icon} size={18} color="#111827" />
        </View>
        <Text style={styles.kpiLabel}>{label}</Text>
      </View>
      <Text style={styles.kpiValue}>{value}</Text>
    </View>
  );

  const renderChart = () => (
    <View style={styles.sectionCard}>
      <View style={styles.sectionHeaderRow}>
        <View style={styles.sectionHeaderLeft}>
          <Ionicons name="bar-chart-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
          <Text style={styles.sectionLabel}>Bookings over time</Text>
        </View>
        <Text style={styles.sectionMeta}>Last 30 days</Text>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.chartRow}>
          {chartData.map((item, index) => {
            const height = Math.max(10, (Number(item.count) / maxChartCount) * 140);
            return (
              <View key={`${item.date}-${index}`} style={styles.chartBarWrap}>
                <View style={[styles.chartBar, { height }]} />
                <Text style={styles.chartValue}>{item.count}</Text>
                <Text style={styles.chartLabel}>{item.date.slice(5)}</Text>
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );

  const renderLeaderboardCard = (title, iconName, rows, labelKey) => (
    <View style={styles.sectionCard}>
      <View style={styles.sectionHeaderRow}>
        <View style={styles.sectionHeaderLeft}>
          <Ionicons name={iconName} size={18} color="#111827" style={{ marginRight: 6 }} />
          <Text style={styles.sectionLabel}>{title}</Text>
        </View>
        <Text style={styles.sectionMeta}>{rows.length} rows</Text>
      </View>

      {rows.length === 0 ? (
        <Text style={styles.emptyText}>No data yet.</Text>
      ) : (
        rows.map((item, index) => (
          <View key={`${item[labelKey]}-${index}`} style={styles.leaderboardRow}>
            <View style={styles.rankBadge}>
              <Text style={styles.rankBadgeText}>{index + 1}</Text>
            </View>

            <View style={styles.leaderboardInfo}>
              <Text style={styles.leaderboardName}>{item[labelKey]}</Text>
              <Text style={styles.leaderboardSub}>{item.bookingCount} bookings</Text>
            </View>
          </View>
        ))
      )}
    </View>
  );

  const renderBrandBreakdown = () => (
    <View style={styles.sectionCard}>
      <View style={styles.sectionHeaderRow}>
        <View style={styles.sectionHeaderLeft}>
          <Ionicons name="business-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
          <Text style={styles.sectionLabel}>Venue breakdown</Text>
        </View>
        <Text style={styles.sectionMeta}>{brandBreakdown.length} venues</Text>
      </View>

      {brandBreakdown.length === 0 ? (
        <Text style={styles.emptyText}>No breakdown data yet.</Text>
      ) : (
        brandBreakdown.map((item, index) => (
          <View key={`${item.BrandName}-${index}`} style={styles.breakdownRow}>
            <View style={styles.breakdownLeft}>
              <Text style={styles.breakdownVenue}>{item.BrandName}</Text>
              <Text style={styles.breakdownMeta}>
                Upcoming: {item.upcomingCount} • Last 30d: {item.last30Count}
              </Text>
            </View>

            <View style={styles.breakdownRightPill}>
              <Text style={styles.breakdownRightPillText}>{item.totalCount}</Text>
            </View>
          </View>
        ))
      )}
    </View>
  );

  const renderRecentBookingsTable = () => (
    <View style={styles.sectionCard}>
      <View style={styles.sectionHeaderRow}>
        <View style={styles.sectionHeaderLeft}>
          <Ionicons name="list-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
          <Text style={styles.sectionLabel}>Recent public bookings</Text>
        </View>
        <Text style={styles.sectionMeta}>{filteredRecentBookings.length} results</Text>
      </View>

      {filteredRecentBookings.length === 0 ? (
        <Text style={styles.emptyText}>No bookings match your search.</Text>
      ) : (
        filteredRecentBookings.map((item, index) => (
          <View key={`${item.Email}-${item.Date}-${index}`} style={styles.tableRowCard}>
            <View style={styles.tableRowTop}>
              <View style={styles.tableTopLeft}>
                <Text style={styles.tableVenue}>{item.BrandName}</Text>
                <Text style={styles.tableOffer}>
                  {(item.VenueOffer || '').length > 100
                    ? `${item.VenueOffer.substring(0, 100)}…`
                    : item.VenueOffer}
                </Text>
              </View>
              <View style={styles.tableDatePill}>
                <Text style={styles.tableDatePillText}>{item.Date?.slice(0, 10)}</Text>
              </View>
            </View>

            <View style={styles.tableMetaRow}>
              <Text style={styles.tableMetaText}>Promoter: {item.Promoter}</Text>
              <Text style={styles.tableMetaText}>Customer: {item.Name}</Text>
            </View>

            <View style={styles.tableMetaRow}>
              <Text style={styles.tableMetaText}>Email: {item.Email}</Text>
              <Text style={styles.tableMetaText}>Phone: {item.Number}</Text>
            </View>

            <View style={styles.tableMetaRow}>
              <Text style={styles.tableMetaText}>Party size: {item.PlusOnes}</Text>
              <Text style={styles.tableMetaText}>Booking for: {item.BooksDate}</Text>
            </View>
          </View>
        ))
      )}
    </View>
  );

  const renderUpcomingSection = () => (
    <View style={styles.sectionCard}>
      <View style={styles.sectionHeaderRow}>
        <View style={styles.sectionHeaderLeft}>
          <Ionicons name="calendar-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
          <Text style={styles.sectionLabel}>Upcoming bookings</Text>
        </View>
        <Text style={styles.sectionMeta}>{upcomingBookings.length} upcoming</Text>
      </View>

      {upcomingBookings.length === 0 ? (
        <Text style={styles.emptyText}>No upcoming bookings.</Text>
      ) : (
        upcomingBookings.map((item, index) => (
          <View key={`${item.Email}-${item.BooksDate}-${index}`} style={styles.upcomingCard}>
            <View style={styles.upcomingCardLeft}>
              <Text style={styles.upcomingVenue}>{item.BrandName}</Text>
              <Text style={styles.upcomingCustomer}>{item.Name}</Text>
              <Text style={styles.upcomingMeta}>By {item.Promoter}</Text>
              <Text style={styles.upcomingMeta}>Party {item.PlusOnes}</Text>
            </View>

            <View style={styles.upcomingCardRight}>
              <Text style={styles.upcomingDate}>{item.BooksDate?.slice(0, 10)}</Text>
              <Text style={styles.upcomingTime}>{item.BooksDate?.slice(11, 16) || '--:--'}</Text>
            </View>
          </View>
        ))
      )}
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.appShell}>
          <View style={styles.shellRow}>
            <View style={styles.sideNav}>
              <LinearGradient colors={['#050509', '#050505', '#000000']} style={styles.sideNavInner}>
                <LinearGradient
                  colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                  style={styles.sideNavGlow}
                  start={{ x: 0.1, y: 0 }}
                  end={{ x: 1, y: 1 }}
                />

                <View style={styles.sideNavHeader}>
                  <Image
                    source={{ uri: 'https://collabbayadmin.com/CollabbayLogoDark.png' }}
                    style={styles.sideNavLogoImage}
                    resizeMode="contain"
                  />
                  <View>
                    <Text style={styles.logoWordmark}>
                      <Text style={{ fontWeight: '300' }}>collab</Text>
                      bay
                      <Text style={styles.logoDot}>.</Text>
                    </Text>
                    <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                  </View>
                </View>

                <View style={styles.sideNavSection}>
                  <LinearGradient
                    colors={['#111111', '#2b2b2b']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.usernamePill}
                  >
                    <Text numberOfLines={1} ellipsizeMode="tail" style={styles.usernamePillText}>
                      {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                    </Text>
                  </LinearGradient>
                </View>
              </LinearGradient>
            </View>

            <View style={styles.mainArea}>
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="black" />
                <Text style={styles.loadingText}>Loading analytics…</Text>
              </View>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.appShell}>
        <View style={styles.shellRow}>
          {/* EXACT SIDENAV CHARACTER */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              <View style={styles.sideNavHeader}>
                <Image
                  source={{ uri: 'https://collabbayadmin.com/CollabbayLogoDark.png' }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              <View style={styles.sideNavSection}>
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text numberOfLines={1} ellipsizeMode="tail" style={styles.usernamePillText}>
                    {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('MyPanel')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="home-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('MyOffers')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="pricetags-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) openInSameTab('https://collabbayadmin.com/subscribe');
                    else navigation.navigate('MyCalendar');
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="calendar-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons name="lock-closed-outline" size={16} color="#FDF05D" style={styles.lockIcon} />
                    )}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('BrowseCreators')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="people-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) openInSameTab('https://collabbayadmin.com/subscribe');
                    else navigation.navigate('MyActivityFeed');
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="chatbubbles-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons name="lock-closed-outline" size={16} color="#FDF05D" style={styles.lockIcon} />
                    )}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('BusinessAccountPage')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="person-circle-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              <View style={styles.sideNavSection}>
                <TouchableOpacity style={styles.peerMapCard} activeOpacity={0.9} onPress={() => openInSameTab('/venues')}>
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons name="map-outline" size={18} color="#FDF05D" style={{ marginRight: 8 }} />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>View 100+ venues on Collabbay</Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() => openInSameTab('https://collabbayadmin.com/subscribe')}
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons name="rocket-outline" size={20} color="#000" style={{ marginRight: 8 }} />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>Subscribe & unlock</Text>
                          <Text style={styles.subscribeSubtitle}>Unlock Activity & Calendar, plus more.</Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.sideNavBottom}>
                <TouchableOpacity style={styles.bottomLink} onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}>
                  <Ionicons name="help-circle-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>Common questions answered</Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.bottomLink} onPress={() => Linking.openURL('mailto:info@collab-bay.com')}>
                  <Ionicons name="headset-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>We&apos;re here 24/7</Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <View style={styles.headerBar}>
              <View style={styles.headerLeft}>
                <TouchableOpacity style={styles.headerBackButton} onPress={handleBackPress}>
                  <Ionicons name="arrow-back" size={20} color="black" />
                </TouchableOpacity>
                <View style={styles.headerTitles}>
                  <Text style={styles.pageTitle}>Public booking analytics</Text>
                  <Text style={styles.pageSubtitle}>
                    View all creator-attributed public bookings across the platform.
                  </Text>
                </View>
              </View>

              <View style={styles.headerRight}>
                {userData?.Username ? (
                  <View style={styles.userBadge}>
                    <View style={styles.userBadgeInitial}>
                      <Text style={styles.userBadgeInitialText}>
                        {userData.Username.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                    <Text style={styles.userBadgeText} numberOfLines={1}>
                      {userData.Username}
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>

            <View style={styles.searchRow}>
              <View style={styles.searchWrapper}>
                <Ionicons name="search-outline" size={18} color="#9ca3af" style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search bookings, venues, creators or emails"
                  placeholderTextColor="#9ca3af"
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>

              <TouchableOpacity style={styles.newOfferButton} onPress={fetchDashboard}>
                <Ionicons name="refresh" size={16} color="#111827" style={{ marginRight: 6 }} />
                <Text style={styles.newOfferButtonText}>Refresh</Text>
              </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
              <View style={styles.kpiGrid}>
                {renderKpiCard('Total bookings', summary.totalBookings, 'pulse-outline')}
                {renderKpiCard('Last 30 days', summary.bookingsLast30Days, 'stats-chart-outline')}
                {renderKpiCard('Last 7 days', summary.bookingsLast7Days, 'flash-outline')}
                {renderKpiCard('Upcoming', summary.upcomingBookings, 'calendar-outline')}
              </View>

              {renderChart()}

              <View style={styles.twoColRow}>
                <View style={styles.twoColItem}>
                  {renderLeaderboardCard('Top creators', 'people-outline', leaderboard, 'Promoter')}
                </View>
                <View style={styles.twoColItem}>
                  {renderLeaderboardCard('Top venues', 'business-outline', venueLeaderboard, 'BrandName')}
                </View>
              </View>

              {renderBrandBreakdown()}
              {renderUpcomingSection()}
              {renderRecentBookingsTable()}
            </ScrollView>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  appShell: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  mainArea: {
    flex: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    backgroundColor: '#e5e7eb',
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  headerBackButton: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  headerTitles: {
    flexShrink: 1,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
  },
  pageSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  headerRight: {
    marginLeft: 10,
  },
  userBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111827',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  userBadgeInitial: {
    width: 22,
    height: 22,
    borderRadius: 999,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  userBadgeInitialText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },
  userBadgeText: {
    fontSize: 11,
    color: '#f9fafb',
    maxWidth: 120,
  },

  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    marginTop: 6,
  },
  searchWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    paddingVertical: 4,
  },
  newOfferButton: {
    marginLeft: 8,
    backgroundColor: '#fdf05d',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
  },
  newOfferButtonText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },

  scrollContent: {
    paddingBottom: 40,
  },

  kpiGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 18,
    flexWrap: 'wrap',
  },
  kpiCard: {
    width: '24%',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#d1d5db',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.06,
    shadowRadius: 5,
    elevation: 2,
  },
  kpiTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  kpiIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  kpiLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
  },
  kpiValue: {
    fontSize: 28,
    fontWeight: '800',
    color: '#111827',
  },

  sectionCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 18,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  sectionMeta: {
    fontSize: 12,
    color: '#6b7280',
  },

  chartRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingTop: 12,
    paddingRight: 16,
  },
  chartBarWrap: {
    width: 34,
    marginRight: 10,
    alignItems: 'center',
  },
  chartBar: {
    width: 22,
    borderRadius: 10,
    backgroundColor: '#111827',
    marginBottom: 6,
  },
  chartValue: {
    fontSize: 11,
    color: '#444',
    marginBottom: 4,
  },
  chartLabel: {
    fontSize: 10,
    color: '#888',
  },

  twoColRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  twoColItem: {
    width: '49.2%',
  },

  leaderboardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 8,
  },
  rankBadge: {
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: '#111827',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  rankBadgeText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 13,
  },
  leaderboardInfo: {
    flex: 1,
  },
  leaderboardName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  leaderboardSub: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },

  breakdownRow: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  breakdownLeft: {
    flex: 1,
    paddingRight: 10,
  },
  breakdownVenue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2,
  },
  breakdownMeta: {
    fontSize: 12,
    color: '#6b7280',
  },
  breakdownRightPill: {
    minWidth: 40,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: '#111827',
    alignItems: 'center',
    justifyContent: 'center',
  },
  breakdownRightPillText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 12,
  },

  upcomingCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  upcomingCardLeft: {
    flex: 1,
    paddingRight: 10,
  },
  upcomingVenue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  upcomingCustomer: {
    fontSize: 13,
    color: '#111827',
    marginBottom: 2,
  },
  upcomingMeta: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  upcomingCardRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  upcomingDate: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },
  upcomingTime: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },

  tableRowCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 10,
  },
  tableRowTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  tableTopLeft: {
    flex: 1,
    paddingRight: 10,
  },
  tableVenue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  tableOffer: {
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 17,
  },
  tableDatePill: {
    alignSelf: 'flex-start',
    backgroundColor: '#111827',
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 999,
  },
  tableDatePillText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  tableMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
    flexWrap: 'wrap',
  },
  tableMetaText: {
    fontSize: 12,
    color: '#6b7280',
    marginRight: 10,
    marginTop: 2,
  },

  emptyText: {
    fontSize: 13,
    color: '#6b7280',
  },

  loadingContainer: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    fontSize: 13,
    color: '#6b7280',
  },
});

export default PublicBookingsAnalyticsDashboard;